import { useEffect, useState } from 'react';
import { Activity, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

type ApiState =
  | { kind: 'loading' }
  | { kind: 'ok'; health: string }
  | { kind: 'error'; message: string };

/** Query the API through a relative URL so the same code works in dev and prod. */
async function fetchApiHealth(): Promise<string> {
  const res = await fetch('/api/graphql', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ query: '{ health }' }),
  });
  if (!res.ok) {
    throw new Error(`API responded with ${res.status}`);
  }
  const payload = (await res.json()) as { data?: { health?: string } };
  return payload.data?.health ?? 'unknown';
}

function StatusRow({ state }: { state: ApiState }) {
  const config = {
    loading: { icon: Loader2, text: 'Checking API…', cls: 'text-muted-foreground', spin: true },
    ok: {
      icon: CheckCircle2,
      text: `API health: ${'health' in state ? state.health : ''}`,
      cls: 'text-emerald-600',
      spin: false,
    },
    error: {
      icon: XCircle,
      text: `API unreachable: ${'message' in state ? state.message : ''}`,
      cls: 'text-destructive',
      spin: false,
    },
  }[state.kind];
  const Icon = config.icon;
  return (
    <div className={cn('flex items-center gap-2 text-sm font-medium', config.cls)}>
      <Icon className={cn('size-4', config.spin && 'animate-spin')} />
      <span>{config.text}</span>
    </div>
  );
}

export function App() {
  const [state, setState] = useState<ApiState>({ kind: 'loading' });

  const refresh = () => {
    setState({ kind: 'loading' });
    fetchApiHealth()
      .then((health) => setState({ kind: 'ok', health }))
      .catch((error: unknown) =>
        setState({ kind: 'error', message: error instanceof Error ? error.message : 'failed' }),
      );
  };

  useEffect(refresh, []);

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-b from-background to-muted p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-2 flex size-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Activity className="size-5" />
          </div>
          <CardTitle>
            <h1 className="text-2xl">dalton-lott.com</h1>
          </CardTitle>
          <CardDescription>Personal website platform — served through CloudFront.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border bg-muted/40 p-4">
            <StatusRow state={state} />
          </div>
        </CardContent>
        <CardFooter className="justify-between">
          <span className="text-xs text-muted-foreground">/api/graphql · relative origin</span>
          <Button size="sm" variant="outline" onClick={refresh} disabled={state.kind === 'loading'}>
            <RefreshCw className={cn(state.kind === 'loading' && 'animate-spin')} />
            Refresh
          </Button>
        </CardFooter>
      </Card>
    </main>
  );
}
